import { isVisibleComponetBaseOnRole } from "@/commons/RoleFunction.ts";
import {
  Field,
  Input,
  IOption,
  Popover,
  PopoverSurface,
  SearchField,
  EAlignment,
  EPosition
} from "@/dependencies";
import {
  createTableColumn,
  FieldProps,
  List,
  ListItem,
  makeStyles,
  mergeClasses,
  Option,
  OptionOnSelectData,
  PositioningImperativeRef,
  SelectionEvents,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableSelectionCell,
  useRestoreFocusTarget,
  useTableFeatures,
  useTableSelection
} from "@fluentui/react-components";
import { ChevronDownRegular } from "@fluentui/react-icons";
import { forwardRef, useEffect, useId, useRef, useState } from "react";
import { FixedSizeList } from "react-window";
import {
  DropdownSearchWrapper,
  ListboxWrapper,
  OptionWrapper,
  SearchWrapper,
  TableVirtualizationWrapper
} from "./Style";
import NoData from "@/dependencies/Nodata";

interface DropdownFieldProps {
  rcName?: string;
  theme?: string;
  name?: string;
  rcRole?: string[];
  requireRoles?: string[];
  requireOwner?: boolean;
  isOwner?: boolean;
  selectedOptions?: string[];
  defaultSelectedOptions?: string[];
  multiselect?: boolean;
  disabled?: boolean;
  options?: IOption[];
  onSelectOption?: (event: SelectionEvents, data: OptionOnSelectData) => void;
  placeholder?: string;
  onOpenChange?: (open: boolean) => void;
  isLazyLoad?: boolean;
  onSelectionMultiChange?: (selectedOptions: string[]) => void;
}

const useStyles = makeStyles({
  root: {
    fontWeight: 600
  },
  dropdown: {
    maxHeight: "300px"
  },
  disabled: {
    backgroundColor: "var(--colorSubtleBackgroundHover)",
    "& input": {
      color: "var(--colorNeutralForeground1)"
    }
  }
});

const DropdownSearchField = (props: DropdownFieldProps & FieldProps) => {
  const styles = useStyles();
  const [options, setOptions] = useState<IOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<IOption[]>([]);
  const [mapOptions, setMapOptions] = useState<Record<string, IOption>>({});
  const wrapperRef = useRef<HTMLInputElement | null>(null);
  const positioningRef = useRef<PositioningImperativeRef>(null);
  const restoreFocusTargetAttribute = useRestoreFocusTarget();
  const [open, setOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const headerId = useId();
  const listRefMultiselect = useRef<FixedSizeList>(null);
  const listRefSingle = useRef<FixedSizeList>(null);
  const optionWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (wrapperRef?.current && positioningRef.current) {
      positioningRef.current?.setTarget(wrapperRef.current);
    }
  }, [wrapperRef, positioningRef]);

  useEffect(() => {
    setSelectedOptions(props.selectedOptions || []);
  }, [props.selectedOptions]);

  useEffect(() => {
    setOptions([...(props.options || [])]);
    setFilteredOptions([...(props.options || [])]);
    const mapTemp: Record<string, IOption> = {};
    props.options?.forEach((item: IOption) => {
      mapTemp[item.value] = item;
    });
    setMapOptions(mapTemp);
  }, [props.options]);

  useEffect(() => {
    // Only auto-scroll for regular (non-lazy) lists
    if (open && !props.isLazyLoad && selectedOptions.length > 0 && filteredOptions.length > 0) {
      // Wait for the popover to be fully rendered
      const timeoutId = setTimeout(() => {
        // Find the first selected option that exists in the filtered options
        const selectedValue = selectedOptions.find((value) =>
          filteredOptions.some((option) => option.value === value)
        );

        if (selectedValue && optionWrapperRef.current) {
          const selectedIndex = filteredOptions.findIndex((option) => option.value === selectedValue);

          if (selectedIndex >= 0) {
            // Try multiple selector strategies
            let optionElement = optionWrapperRef.current.querySelector(
              `[data-option-value="${selectedValue}"]`
            ) as HTMLElement;
            
            // Fallback: find by index position
            if (!optionElement) {
              const allOptions = optionWrapperRef.current.querySelectorAll('[role="option"]');
              if (allOptions[selectedIndex]) {
                optionElement = allOptions[selectedIndex] as HTMLElement;
              }
            }
            
            // Another fallback: find by text content
            if (!optionElement) {
              const selectedOption = filteredOptions[selectedIndex];
              if (selectedOption) {
                const options = optionWrapperRef.current.querySelectorAll('[role="option"]');
                Array.from(options).forEach((el) => {
                  if (el.textContent?.trim() === selectedOption.text) {
                    optionElement = el as HTMLElement;
                  }
                });
              }
            }
            
            if (optionElement) {
              optionElement.scrollIntoView({ block: "nearest" });
            }
          }
        }
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [open, selectedOptions, filteredOptions, props.isLazyLoad]);

  const onOptionSelect = (event: SelectionEvents, data: OptionOnSelectData) => {
    if (props.onSelectOption) {
      props.onSelectOption(event, data);
    }
  };

  const getWithElement = (): number => {
    const el = document.getElementById(`select_context_${props.rcName || "input"}`);
    return el?.offsetWidth ? el.offsetWidth : 0;
  };

  const onSearch = (value: string) => {
    setSearchValue(value);
    const filteredOptions = options.filter((option) =>
      option.text.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredOptions(filteredOptions);
  };

  const IconList = forwardRef<HTMLUListElement>((props: React.ComponentProps<typeof List>, ref) => (
    <List role='grid' aria-label='icon-list' tabIndex={0} {...props} ref={ref} />
  ));

  const isCanBeVisible =
    isVisibleComponetBaseOnRole(props.rcRole, props.requireRoles) &&
    (props.requireOwner ? props.isOwner : true);

  const columns = [
    createTableColumn<IOption>({
      columnId: "selection-all"
    })
  ];

  const {
    selection: { allRowsSelected, someRowsSelected, toggleAllRows, toggleRow, isRowSelected }
  } = useTableFeatures(
    {
      columns,
      items: options,
      getRowId: (option: IOption) => option.value
    },
    [
      useTableSelection({
        selectionMode: "multiselect",
        selectedItems: new Set(selectedOptions || []),
        onSelectionChange: (_e, item) => {
          const selectedItems = Array.from(item.selectedItems).map((item) => item.toString());
          setSelectedOptions(selectedItems);
          if (props.onSelectionMultiChange) {
            props.onSelectionMultiChange(selectedItems);
          }
        }
      })
    ]
  );

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const renderRow = ({ index, style, data }: any) => {
    const item = data[index];
    return (
      <TableRow
        aria-rowindex={index + 2}
        style={style}
        key={`row-${item.value || "value"}-${index}`}
      >
        <TableSelectionCell
          checked={isRowSelected(item.value)}
          onClick={(e) => toggleRow(e, item.value)}
          checkboxIndicator={{ "aria-label": "Select row" }}
        />
        <TableCell>{item.text}</TableCell>
      </TableRow>
    );
  };

  const renderListLazyLoadMultiselect = () => {
    return (
      <TableVirtualizationWrapper>
        <Table
          noNativeElements
          aria-label='Table with selection'
          aria-rowcount={filteredOptions.length}
          style={{ minWidth: "100%" }}
        >
          <TableHeader>
            <TableRow aria-rowindex={1}>
              <TableSelectionCell
                checked={allRowsSelected ? true : someRowsSelected ? "mixed" : false}
                onClick={toggleAllRows}
                checkboxIndicator={{ "aria-label": "Select all rows" }}
              />

              <TableHeaderCell>Select All</TableHeaderCell>
              {/** Scrollbar alignment for the header */}
              <div role='presentation' />
            </TableRow>
          </TableHeader>
          <TableBody>
            <FixedSizeList
              ref={listRefMultiselect}
              height={300}
              itemCount={filteredOptions.length}
              itemSize={45}
              width='100%'
              itemData={filteredOptions}
              className='scroll-bar-custom'
            >
              {renderRow}
            </FixedSizeList>
          </TableBody>
        </Table>
      </TableVirtualizationWrapper>
    );
  };

  const renderListLazyLoad = () => {
    return (
      <FixedSizeList
        ref={listRefSingle}
        height={300}
        itemCount={filteredOptions.length}
        itemSize={32}
        width='100%'
        itemData={filteredOptions}
        outerElementType={IconList}
        layout='vertical'
        className='scroll-bar-custom'
      >
        {({
          index,
          style,
          data
        }: {
          index: number;
          style: React.CSSProperties;
          data: IOption[];
        }) => (
          <ListItem style={style} aria-setsize={filteredOptions.length} aria-posinset={index + 1}>
            <Option
              key={data[index].value}
              value={data[index].value}
              text=''
              onClick={(e) => {
                onOptionSelect(e, {
                  optionValue: data[index].value,
                  optionText: data[index].text,
                  selectedOptions: []
                });
                setOpen(false);
              }}
              className={selectedOptions[0] === data[index].value ? "selected" : ""}
            >
              {data[index].text}
            </Option>
          </ListItem>
        )}
      </FixedSizeList>
    );
  };

  const renderList = () => {
    if (!filteredOptions.length) {
      return <NoData size='medium' />;
    }
    if (props.isLazyLoad) {
      return props.multiselect ? renderListLazyLoadMultiselect() : renderListLazyLoad();
    }
    return (
      <>
        {(filteredOptions || [])?.map((option: IOption) => (
          <Option
            key={option.value}
            value={option.value}
            text=''
            data-option-value={option.value}
            onClick={(e) => {
              onOptionSelect(e, {
                optionValue: option.value,
                optionText: option.text,
                selectedOptions: []
              });
              setOpen(false);
            }}
            className={selectedOptions[0] === option.value ? "selected" : ""}
          >
            {option.text}
          </Option>
        ))}
      </>
    );
  };

  const isDisabled = props.disabled || !isCanBeVisible;

  return (
    <DropdownSearchWrapper
      id={`select_context_${props.rcName || "input"}`}
      className={mergeClasses(props.className, "DropdownSearchWrapper")}
    >
      <Field
        label={props.label}
        validationMessage={props.validationMessage}
        validationMessageIcon={props.validationMessageIcon}
        validationState={props.validationState}
        required={props.required}
        style={props.style}
        className={styles.root}
      >
        <div ref={wrapperRef}>
          <Input
            {...restoreFocusTargetAttribute}
            onClick={() => {
              setOpen((s) => !s);
              if (props.onOpenChange) {
                props.onOpenChange(!open);
              }
            }}
            style={{ width: "100%", cursor: "pointer" }}
            disabled={isDisabled}
            value={
              !props.multiselect
                ? mapOptions[selectedOptions[0]]?.text || ""
                : selectedOptions.map((option) => mapOptions[option]?.text).join(", ")
            }
            contentAfter={
              <ChevronDownRegular
                onClick={() => {
                  setOpen((s) => !s);
                  if (props.onOpenChange) {
                    props.onOpenChange(!open);
                  }
                }}
              />
            }
            readOnly
            className={`${isDisabled ? styles.disabled : ""}`}
            placeholder={props.placeholder}
          />
        </div>
      </Field>
      <Popover
        onOpenChange={(_, data) => {
          setOpen(data.open);
          if (props.onOpenChange) {
            props.onOpenChange(data.open);
          }
        }}
        trapFocus
        open={open}
        positioning={{
          positioningRef,
          position: EPosition.BELOW,
          align: EAlignment.START
        }}
        mountNode={undefined}
      >
        <PopoverSurface
          aria-labelledby={headerId}
          className='width_custom_popover'
          style={{
            width: getWithElement(),
            padding: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <ListboxWrapper
            className='ListboxWrapper'
            style={{ display: "flex", flexDirection: "column", overflow: "hidden", width: "100%" }}
          >
            <SearchWrapper className='SearchWrapper' style={{ flexShrink: 0, marginBottom: "8px" }}>
              <SearchField onSearched={onSearch} value={searchValue} />
            </SearchWrapper>
            <OptionWrapper
              ref={optionWrapperRef}
              className='OptionWrapper custom-scrollbar'
              style={{
                maxHeight: props.isLazyLoad ? "100%" : "300px",
                overflow: "auto",
                padding: "0px",
                flex: 1,
                minHeight: 0,
                width: "100%"
              }}
            >
              {renderList()}
            </OptionWrapper>
          </ListboxWrapper>
        </PopoverSurface>
      </Popover>
    </DropdownSearchWrapper>
  );
};

export default DropdownSearchField;
